import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Home, Download, Share2, Mail, FileText, Image as ImageIcon, Printer, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MediaKit = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [mediaKit, setMediaKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchListingAndKit();
  }, [id]);

  const fetchListingAndKit = async () => {
    try {
      const [listingRes, kitRes] = await Promise.all([
        axios.get(`${API}/listings/${id}`),
        axios.get(`${API}/media-kit/${id}`, { withCredentials: true }).catch(() => null)
      ]);
      setListing(listingRes.data);
      if (kitRes) {
        setMediaKit(kitRes.data);
      }
    } catch (error) {
      toast.error("Failed to load listing");
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const generateKit = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/media-kit/${id}/generate`, {}, { withCredentials: true });
      setMediaKit(response.data);
      toast.success("Media kit generated! 🎉");
    } catch (error) {
      toast.error("Failed to generate media kit");
    } finally {
      setGenerating(false);
    }
  };

  const downloadItem = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" data-testid="back-dashboard-btn">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                Marketing Media Kit
              </h1>
              <p className="text-gray-600">Professional marketing materials for: {listing?.title}</p>
            </div>
            {!mediaKit && (
              <Button 
                onClick={generateKit}
                disabled={generating}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="generate-kit-btn"
              >
                {generating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Media Kit
                  </>
                )}
              </Button>
            )}
          </div>
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm">
            <Sparkles className="w-4 h-4" />
            <span>AI-generated marketing materials customized for your property</span>
          </div>
        </div>

        {!mediaKit ? (
          <Card className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-purple-100 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-12 h-12 text-purple-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Playfair Display' }}>
              Generate Your Marketing Kit
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              Click the button above to generate professional marketing materials including social media posts, 
              email templates, flyers, and more - all customized for your property using AI.
            </p>
          </Card>
        ) : (
          <Tabs defaultValue="social" className="space-y-6">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="social" data-testid="tab-social">
                <Share2 className="w-4 h-4 mr-2" />
                Social Media
              </TabsTrigger>
              <TabsTrigger value="email" data-testid="tab-email">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="flyer" data-testid="tab-flyer">
                <FileText className="w-4 h-4 mr-2" />
                Flyers
              </TabsTrigger>
              <TabsTrigger value="signs" data-testid="tab-signs">
                <ImageIcon className="w-4 h-4 mr-2" />
                Signs
              </TabsTrigger>
              <TabsTrigger value="openhouse" data-testid="tab-openhouse">
                <Home className="w-4 h-4 mr-2" />
                Open House
              </TabsTrigger>
            </TabsList>

            {/* Social Media Tab */}
            <TabsContent value="social" className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Facebook Post</h3>
                  <div className="space-x-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(mediaKit.social_facebook)}>
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadItem(mediaKit.social_facebook, 'facebook-post.txt')}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap" data-testid="facebook-content">{mediaKit.social_facebook}</p>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Instagram Caption</h3>
                  <div className="space-x-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(mediaKit.social_instagram)}>
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadItem(mediaKit.social_instagram, 'instagram-caption.txt')}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap" data-testid="instagram-content">{mediaKit.social_instagram}</p>
                </div>
              </Card>

              <Card className="p-6 bg-blue-50 border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">💡 Pro Tips</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Post on Facebook during weekday evenings (6-8 PM) for maximum engagement</li>
                  <li>• Use your best photo as the first image</li>
                  <li>• Share in local DFW buy/sell groups</li>
                  <li>• Tag your location for local visibility</li>
                </ul>
              </Card>
            </TabsContent>

            {/* Email Tab */}
            <TabsContent value="email" className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Buyer Outreach Email</h3>
                  <div className="space-x-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(mediaKit.email_template)}>
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadItem(mediaKit.email_template, 'buyer-email.txt')}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap" data-testid="email-content">{mediaKit.email_template}</p>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Neighbor Alert Email</h3>
                  <div className="space-x-2">
                    <Button size="sm" variant="outline" onClick={() => copyToClipboard(mediaKit.neighbor_email)}>
                      Copy
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => downloadItem(mediaKit.neighbor_email, 'neighbor-email.txt')}>
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap" data-testid="neighbor-email-content">{mediaKit.neighbor_email}</p>
                </div>
              </Card>
            </TabsContent>

            {/* Flyer Tab */}
            <TabsContent value="flyer">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Property Flyer Content</h3>
                  <Button variant="outline" onClick={() => downloadItem(mediaKit.flyer_content, 'property-flyer.txt')}>
                    <Printer className="w-4 h-4 mr-2" />
                    Print Flyer
                  </Button>
                </div>
                <div className="bg-white border-4 border-gray-300 p-8 rounded-lg shadow-lg">
                  <div className="text-center mb-6">
                    <h2 className="text-3xl font-bold text-orange-600 mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      ${listing.price.toLocaleString()}
                    </h2>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{listing.title}</h3>
                    <p className="text-gray-600">{listing.address}, {listing.city}, TX {listing.zip_code}</p>
                  </div>
                  <div className="grid grid-cols-4 gap-4 mb-6 text-center">
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{listing.bedrooms}</div>
                      <div className="text-sm text-gray-600">Beds</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{listing.bathrooms}</div>
                      <div className="text-sm text-gray-600">Baths</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{listing.sqft.toLocaleString()}</div>
                      <div className="text-sm text-gray-600">Sqft</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{listing.year_built}</div>
                      <div className="text-sm text-gray-600">Built</div>
                    </div>
                  </div>
                  <div className="mb-6">
                    <p className="text-gray-700 leading-relaxed" data-testid="flyer-content">{mediaKit.flyer_content}</p>
                  </div>
                  <div className="border-t pt-4 text-center">
                    <p className="font-semibold text-gray-900 mb-1">{listing.contact_name}</p>
                    <p className="text-gray-600">{listing.contact_phone} • {listing.contact_email}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mt-4">Print this on 8.5x11" paper and post in local coffee shops, community boards, and mail to neighbors.</p>
              </Card>
            </TabsContent>

            {/* Signs Tab */}
            <TabsContent value="signs">
              <Card className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yard Sign Design</h3>
                <div className="bg-orange-600 text-white p-12 rounded-lg text-center shadow-2xl">
                  <h2 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>FOR SALE</h2>
                  <h3 className="text-4xl font-bold mb-6">${listing.price.toLocaleString()}</h3>
                  <div className="bg-white text-orange-600 px-6 py-3 rounded-full inline-block text-2xl font-bold mb-6">
                    {listing.bedrooms} BD • {listing.bathrooms} BA • {listing.sqft.toLocaleString()} SF
                  </div>
                  <div className="text-3xl font-bold mb-2">{listing.contact_phone}</div>
                  <div className="text-xl">Its-Sold.com/{id}</div>
                </div>
                <div className="mt-4 space-y-2">
                  <p className="text-sm text-gray-600">• Order 18x24" corrugated plastic signs from VistaPrint or Signs.com</p>
                  <p className="text-sm text-gray-600">• Place in front yard and at major intersections (check local regulations)</p>
                  <p className="text-sm text-gray-600">• Cost: ~$20-30 per sign</p>
                </div>
              </Card>
            </TabsContent>

            {/* Open House Tab */}
            <TabsContent value="openhouse" className="space-y-6">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Open House Invitation</h3>
                  <Button variant="outline" onClick={() => copyToClipboard(mediaKit.open_house_invite)}>
                    Copy
                  </Button>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <p className="text-gray-800 whitespace-pre-wrap" data-testid="openhouse-content">{mediaKit.open_house_invite}</p>
                </div>
              </Card>

              <Card className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Open House Checklist</h3>
                <div className="space-y-3">
                  {[
                    "Post signs at major intersections 2 hours before",
                    "Balloons on mailbox for visibility",
                    "Fresh cookies or coffee for visitors",
                    "Information sheets with flyer content",
                    "Sign-in sheet for visitor contact info",
                    "Open all blinds/curtains for natural light",
                    "Music playing softly in background",
                    "Remove pets from home during showing"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center space-x-3">
                      <input type="checkbox" className="w-5 h-5 text-orange-600" />
                      <label className="text-gray-700">{item}</label>
                    </div>
                  ))}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
};

export default MediaKit;
